import { Timezone } from "@nlc/shared-types";
import dayjs from "dayjs";
import { useState } from "react";

import type { ResourceType } from "../../types";
import type { GroupedLogs } from "../../utils/message-parsers";

import { DISABLE_SHOW_MORE } from "../../types";
import { ChatLog } from "./ChatLog";

interface SystemLogsGroupProps {
  data: GroupedLogs;
  isCostumer: boolean;
  maxVisible?: number;
  timeZone: null | string;
}

export function SystemLogsGroup({
  data,
  isCostumer,
  maxVisible = 3,
  timeZone,
}: SystemLogsGroupProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleLogs = isExpanded ? data.items : data.items.slice(0, maxVisible);
  const hasMoreLogs = data.items.length > maxVisible;

  if (data.items.length === 0) {
    return null;
  }

  const timezone = isCostumer ? (timeZone ?? Timezone.EST) : Timezone.EST;

  return (
    <div className="flex flex-col gap-2">
      {visibleLogs.map(
        (log: {
          asanaResourceSubtype?: null | ResourceType | undefined;
          id: string;
          message: string;
          newValue?: string;
          oldValue?: string;
          timestamp: Date;
        }) => (
          <ChatLog
            diffModalContent={
              log.oldValue
                ? {
                    newValue: log.newValue || "",
                    oldValue: log.oldValue || "",
                  }
                : undefined
            }
            key={log.id}
            message={log.message}
            shouldShowMore={DISABLE_SHOW_MORE.has(
              log.asanaResourceSubtype ?? "",
            )}
            timestamp={dayjs(log.timestamp).tz(timezone).fromNow()}
            tooltipDate={dayjs(log.timestamp)
              .tz(timezone)
              .format("DD/MM/YYYY HH:mm")}
          />
        ),
      )}

      {hasMoreLogs && (
        <div className="w-full flex justify-center">
          <button
            className="text-utility-brand-700 font-semibold flex items-center gap-1 text-sm my-2 cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
            type="button"
          >
            {isExpanded
              ? "Show less"
              : `Show ${data.items.length - maxVisible} more`}
            <svg
              className={`inline-block transition-transform ${isExpanded ? "rotate-180" : ""}`}
              fill="none"
              height="20"
              stroke="currentColor"
              viewBox="0 0 24 24"
              width="20"
            >
              <path
                d="M19 9l-7 7-7-7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
